import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, ArrowUpRight, ArrowLeft, Sparkles, Loader2, CheckCircle2, XCircle, Trophy } from "lucide-react";

const STAGE_COLORS = {
  new: "bg-slate-100 text-slate-700",
  screened: "bg-blue-50 text-blue-700",
  interview: "bg-amber-50 text-amber-700",
  offer: "bg-purple-50 text-purple-700",
  hired: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

const VERDICT_COLORS = {
  excellent: "bg-emerald-100 text-emerald-800 border-emerald-200",
  bon: "bg-blue-100 text-blue-800 border-blue-200",
  moyen: "bg-amber-100 text-amber-800 border-amber-200",
  faible: "bg-red-100 text-red-800 border-red-200",
};

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cv_text: "", file: null });
  const [loading, setLoading] = useState(false);
  const [ranking, setRanking] = useState(null);
  const [ranking_loading, setRankingLoading] = useState(false);

  const load = async () => {
    const [j, c] = await Promise.all([api.get(`/jobs/${jobId}`), api.get(`/candidates?job_id=${jobId}`)]);
    setJob(j.data);
    setCandidates(c.data);
    if (j.data.ai_ranking) setRanking(j.data.ai_ranking);
  };
  useEffect(() => { load(); }, [jobId]);

  const submitCandidate = async () => {
    if (!form.full_name) { toast.error("Nom requis"); return; }
    if (!form.cv_text && !form.file) { toast.error("CV requis (fichier ou texte)"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("job_id", jobId);
      fd.append("full_name", form.full_name);
      if (form.email) fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      if (form.cv_text) fd.append("cv_text", form.cv_text);
      if (form.file) fd.append("file", form.file);
      await api.post("/candidates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Candidat ajouté");
      setOpen(false);
      setForm({ full_name: "", email: "", phone: "", cv_text: "", file: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const rankCandidates = async () => {
    setRankingLoading(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/rank-candidates`);
      setRanking(data);
      toast.success(`${data.ranking?.length || 0} candidat(s) classé(s) par l'IA`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur IA");
    } finally { setRankingLoading(false); }
  };

  const assignCandidate = async (candId) => {
    const fd = new FormData();
    fd.append("job_id", jobId);
    try {
      await api.patch(`/candidates/${candId}/assign-job`, fd);
      toast.success("Candidat assigné à ce poste");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  if (!job) return <div className="p-10 text-slate-500">Chargement…</div>;

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <Link to="/app/jobs" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-6 link-underline" data-testid="job-back-link">
        <ArrowLeft className="w-3 h-3" /> Postes
      </Link>

      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-2">Poste</div>
          <h1 className="font-display text-4xl tracking-tight" data-testid="job-title">{job.title}</h1>
          {job.location && <div className="text-slate-500 mt-2 font-mono text-sm">{job.location}{job.salary_range ? ` · ${job.salary_range}` : ""}</div>}
          <p className="text-slate-600 mt-4 leading-relaxed">{job.description}</p>
          {job.must_have?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs tracking-wider uppercase text-slate-500 mb-2">Must-have</div>
              <div className="flex flex-wrap gap-2">
                {job.must_have.map((m, i) => (
                  <Badge key={i} className="bg-slate-900 text-white hover:bg-slate-900 font-mono text-xs">{m}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={rankCandidates} disabled={ranking_loading} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="rank-candidates-btn">
            {ranking_loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse IA…</> : <><Sparkles className="w-4 h-4 mr-2" /> Trouver le meilleur candidat</>}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-pill" data-testid="add-candidate-btn">
                <Upload className="w-4 h-4 mr-2" /> Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Nouveau candidat</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Nom complet *</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" data-testid="cand-form-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="cand-form-email" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Téléphone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="cand-form-phone" /></div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Fichier CV (PDF ou DOCX)</Label>
                  <input type="file" accept=".pdf,.docx" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="mt-1.5 block w-full text-sm" data-testid="cand-form-file" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Ou coller texte du CV</Label>
                  <Textarea value={form.cv_text} onChange={(e) => setForm({ ...form, cv_text: e.target.value })} className="mt-1.5 min-h-[120px] font-mono text-xs" data-testid="cand-form-text" />
                </div>
                <Button onClick={submitCandidate} disabled={loading} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill h-11" data-testid="cand-form-submit">
                  {loading ? "Envoi…" : "Ajouter le candidat"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {ranking && ranking.ranking?.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="font-display text-2xl tracking-tight">Classement IA des candidats</h2>
          </div>
          {ranking.global_note && <p className="text-slate-600 mb-4 italic">{ranking.global_note}</p>}
          <div className="space-y-4">
            {ranking.ranking.map((r, idx) => {
              const isBest = r.candidate_id === ranking.best_candidate_id;
              return (
                <div key={r.candidate_id} className={`border ${isBest ? "border-emerald-400 ring-2 ring-emerald-100" : "border-slate-200"} bg-white p-6 relative`} data-testid={`ranking-${idx}`}>
                  {isBest && (
                    <div className="absolute -top-3 left-6 bg-emerald-600 text-white text-[10px] tracking-widest uppercase px-3 py-1">
                      🏆 Meilleur match
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div className="flex items-center gap-4">
                      <div className="font-mono text-3xl font-bold text-slate-300 tabular-nums w-10 text-right">#{idx + 1}</div>
                      <div>
                        <div className="font-display text-xl">{r.candidate_name}</div>
                        <div className="text-xs text-slate-500 font-mono mt-1">{r.candidate_email || "—"}</div>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <Badge className={`${VERDICT_COLORS[r.verdict] || "bg-slate-100"} border font-mono text-xs`}>{r.verdict}</Badge>
                          <Badge className="bg-slate-900 text-white hover:bg-slate-900 font-mono text-xs">{r.recommendation}</Badge>
                          {r.must_have_check?.all_passed ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-50 font-mono text-xs"><CheckCircle2 className="w-3 h-3 mr-1" /> Must-have ✓</Badge>
                          ) : (
                            <Badge className="bg-red-50 text-red-700 border border-red-100 hover:bg-red-50 font-mono text-xs"><XCircle className="w-3 h-3 mr-1" /> Must-have ✗</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase text-slate-500 tracking-wider">Score match</div>
                      <div className="font-mono text-5xl font-bold tabular-nums">{r.score}<span className="text-lg text-slate-400">/100</span></div>
                    </div>
                  </div>
                  {r.summary && <p className="text-slate-700 mb-4">{r.summary}</p>}

                  {r.matched_skills?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Compétences prouvées</div>
                      <div className="space-y-2">
                        {r.matched_skills.map((s, i) => (
                          <div key={i} className="border border-emerald-100 bg-emerald-50/40 p-3">
                            <div className="flex items-center gap-2 font-medium text-sm">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                              {s.skill}
                              {s.strength && <Badge className="bg-slate-900 text-white hover:bg-slate-900 font-mono text-[10px] ml-1">{s.strength}</Badge>}
                            </div>
                            <div className="text-xs text-slate-600 mt-1.5 italic font-mono pl-6">"{s.evidence}"</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {r.missing_skills?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Compétences manquantes</div>
                      <div className="flex flex-wrap gap-2">
                        {r.missing_skills.map((s, i) => <Badge key={i} className="bg-red-50 text-red-700 border border-red-100 hover:bg-red-50 font-mono text-xs">{s}</Badge>)}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button onClick={() => navigate(`/app/candidates/${r.candidate_id}`)} variant="outline" size="sm" className="btn-pill" data-testid={`view-cand-${idx}`}>
                      Voir le profil complet
                    </Button>
                    {!candidates.find((c) => c.id === r.candidate_id) && (
                      <Button onClick={() => assignCandidate(r.candidate_id)} size="sm" className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid={`assign-cand-${idx}`}>
                        Assigner à ce poste
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl tracking-tight">Candidats assignés <span className="text-slate-400 font-mono text-sm">({candidates.length})</span></h2>
      </div>

      {candidates.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center bg-white">
          <FileText className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">Aucun candidat assigné à ce poste. Lancez l'IA pour trouver le meilleur match dans votre pool.</p>
          <Button onClick={rankCandidates} disabled={ranking_loading} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="empty-rank-btn">
            {ranking_loading ? "Analyse…" : (<><Sparkles className="w-4 h-4 mr-2" /> Trouver le meilleur candidat</>)}
          </Button>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white">
          {candidates.map((c) => {
            const score = c.ai?.justification?.match_score;
            return (
              <Link key={c.id} to={`/app/candidates/${c.id}`} className="flex items-center gap-4 p-5 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 group" data-testid={`cand-row-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{c.full_name}</div>
                  <div className="text-xs text-slate-500 mt-1 font-mono truncate">{c.email || "—"}</div>
                </div>
                <div className="w-28 text-right">
                  {score != null ? (
                    <div className="font-mono text-2xl font-bold tabular-nums">{score}<span className="text-xs text-slate-400">/100</span></div>
                  ) : (
                    <div className="text-xs text-slate-400 italic">Non analysé</div>
                  )}
                </div>
                <Badge className={`${STAGE_COLORS[c.stage] || "bg-slate-100 text-slate-700"} hover:opacity-100 font-mono text-xs`}>{c.stage}</Badge>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
